/**
 * Session token helpers. The token lives in sessionStorage only —
 * cleared on tab close, never persisted to localStorage or cookies.
 *
 * A module-scope listener set publishes token changes so `useAuth` can
 * subscribe via useSyncExternalStore. This makes every component that
 * reads the token re-render when setToken / clearToken / wipeSession fires
 * — previously only the caller's local useState updated, which meant the
 * AppShell's CacheMenu icon stayed hidden until the next full reload.
 */

import { clearRecentRepos } from "@/lib/recent-repos";

const TOKEN_KEY = "gha_token";

let cachedToken: string | null | undefined;
const listeners = new Set<() => void>();

function readFromStorage(): string | null {
  if (typeof sessionStorage === "undefined") return null;
  return sessionStorage.getItem(TOKEN_KEY);
}

function notify(): void {
  for (const cb of listeners) cb();
}

export function getToken(): string | null {
  if (cachedToken === undefined) cachedToken = readFromStorage();
  return cachedToken;
}

export function setToken(token: string): void {
  if (typeof sessionStorage === "undefined") return;
  sessionStorage.setItem(TOKEN_KEY, token);
  cachedToken = token;
  notify();
}

export function clearToken(): void {
  if (typeof sessionStorage === "undefined") return;
  sessionStorage.removeItem(TOKEN_KEY);
  cachedToken = null;
  notify();
}

/**
 * Synchronous sessionStorage wipe for every known `gha_*` key. Does NOT
 * touch the IndexedDB caches — callers that need the IDB wipe must call
 * `clearPersistedCache()` explicitly.
 */
export function wipeSession(): void {
  if (typeof sessionStorage === "undefined") return;
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem("gha_oauth_state");
  // Delegates so subscribers of the recent-repos store are notified too.
  clearRecentRepos();
  cachedToken = null;
  notify();
}

/** Subscribe to token mutations. Returns an unsubscribe function. */
export function subscribeTokenChange(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}
